import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Card, Col, Flex, Form, Row, Typography, message } from "antd";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { CustomInputNumber } from "../../../../../shared/formFields/CustomInputNumber";
import { navigateToPreviousPage } from "../../../../../store/aviationForm/aviationFormSlice";
import { useSaveApplicationMutation } from "../../../../../store/baseApi";
import { emailConfirmationSchema } from "../formSchema";
import { ReloadOutlined } from "@ant-design/icons";
import React, { useState } from "react";
import { resendVerificationCode } from "../../../../../store/aviationForm/resendVerificationCode";
import { useParams } from "react-router-dom";

export const EmailConfirmationPage = () => {
  const { applicationId } = useParams();
  const dispatch = useDispatch();
  const data = useSelector((state) => state.aviationForm.data);

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    formState: { isValid, isSubmitted },
  } = useForm({
    mode: "onChange",
    defaultValues: data,
    values: {
      ...data,
      verification_code: "", // forces verification code field to be blank
    },
    resolver: yupResolver(emailConfirmationSchema),
  });

  const [saveApplication] = useSaveApplicationMutation();

  // Track when the last verification email was sent
  const [lastSentAt, setLastSentAt] = useState(Date.now());

  const handleResend = () => {
    const now = Date.now();
    const secondsElapsed = Math.floor((now - lastSentAt) / 1000);

    if (secondsElapsed < 30) {
      message.warning(
        `Please wait ${30 - secondsElapsed} seconds before resending the code.`
      );
      return;
    }

    setLastSentAt(now);
    dispatch(resendVerificationCode({ id: applicationId }))
      .unwrap()
      .then(() => {
        message.success("Verification code resent.");
      })
      .catch((err) => {
        message.error(
          err?.message ||
            "Unable to resend verification code. Please try again."
        );
      });
  };

  const onFinish = (values) => {
    saveApplication({
      content: { ...values, step: values.step + 1 },
    });
  };

  return (
    <div className="centered-body-background">
      <Flex justify="center" align="center" vertical>
        <div
          style={{
            marginTop: 52,
            maxWidth: 450,
            marginLeft: 18,
            marginRight: 18,
          }}
        >
          <FormProvider
            control={control}
            watch={watch}
            trigger={trigger}
            isSubmitted={isSubmitted}
          >
            <Form
              name="email_confirmation_form"
              autoComplete="off"
              layout="vertical"
              labelWrap
              onFinish={() => {
                handleSubmit(onFinish, () => {})();
              }}
              size="medium"
            >
              <Card>
                <Row justify="space-between">
                  <Col>
                    <Typography.Title
                      level={4}
                      style={{ marginTop: 0, marginBottom: 12 }}
                    >
                      Email confirmation
                    </Typography.Title>
                  </Col>
                </Row>

                <Typography.Text type="primary">
                  We’ve sent a verification code to your email address and you
                  should receive it shortly. If it hasn't arrived after a
                  minute, check your spam folder or click Resend code. Please
                  enter the code below to confirm your email address and
                  finalize your application (<b>{data.email}</b>).
                </Typography.Text>
                <Row style={{ marginTop: 24 }}>
                  <Col span={24}>
                    <Row span={24}>
                      <Col span={24}>
                        <CustomInputNumber
                          formItem={{
                            required: true,
                            label: "Verification code",
                          }}
                          control={control}
                          name="verification_code"
                          placeholder="Enter your verification code"
                        />
                      </Col>
                    </Row>

                    <Row justify="end">
                      <Col>
                        <Button
                          type="link"
                          style={{
                            marginRight: 0,
                            marginLeft: 0,
                            paddingRight: 0,
                            paddingLeft: 0,
                          }}
                          onClick={handleResend}
                        >
                          <ReloadOutlined /> Resend verification code
                        </Button>
                      </Col>
                    </Row>
                    <div style={{ marginBottom: 24 }} />
                    <Row justify="end" gutter={[8, 8]}>
                      <Col>
                        <Button
                          onClick={() => {
                            dispatch(navigateToPreviousPage());
                          }}
                        >
                          Previous
                        </Button>
                      </Col>

                      <Col>
                        <Button
                          htmlType="submit"
                          type="primary"
                          disabled={!isValid}
                        >
                          {data?.will_skip_to_submission ? "Submit Application" : "Confirm"}
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Card>
            </Form>
          </FormProvider>
        </div>
      </Flex>
    </div>
  );
};
